import { useEffect, useMemo, useRef } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  formatAuthorLine,
  toGalleryImages,
  toSensitiveImageUrls,
  toTagFacets,
} from '@/api/adapters'
import { hasProblemCode, toMessage } from '@/api/problem'
import { usePostDetailQuery, useRecordViewMutation, useToggleFavoriteMutation } from '@/api/queries'
import { ClockIcon, DownloadIcon, FavoriteIcon, UserIcon, ViewsIcon } from '@/components/icons'
import { RenderedHtmlContent } from '@/components/RenderedHtmlContent'
import {
  AdSlot,
  Badge,
  Button,
  ButtonLink,
  ErrorState,
  Panel,
  Skeleton,
  notifyError,
  notifySuccess,
} from '@/components/ui'
import { SITE_NAME, usePageMeta } from '@/hooks'
import { useAuth } from '@/stores/AuthProvider'
import type { PublicPostDetail } from '@/types/api'
import { formatBytes, formatCompactCount, formatDate } from '@/utils/format'
import { CommentSection } from './CommentSection'
import { Gallery } from './Gallery'
import { TagGroups } from './TagGroups'

/**
 * 资源详情页（设计稿 D2 桌面 / M4 移动）。
 *
 * 桌面三栏：左侧导航（AppShell 提供）+ 中间主内容 + 右侧标签与元信息。
 * 移动端单列，主操作按钮吸底（设计稿 M4 底部那条「收藏 / 原帖 / 下载」）。
 *
 * 页面加载时登记一次阅读（`POST /posts/{slug}/view-events`）。后端按
 * (post, 匿名 ID, UTC 日) 去重且日期由服务端生成，所以前端只管发一次、不做本地
 * 节流 —— 自己判重反而可能和服务端的口径不一致。
 */
export function PostDetailPage() {
  const { slug = '' } = useParams<{ slug: string }>()
  const query = usePostDetailQuery(slug)
  const recordView = useRecordViewMutation()

  // 每个 slug 只登记一次：StrictMode 下 effect 会跑两遍，ref 挡住第二次。
  const viewedSlugRef = useRef<string | null>(null)
  useEffect(() => {
    if (!slug || !query.data || viewedSlugRef.current === slug) return
    viewedSlugRef.current = slug
    // 登记失败不影响阅读体验，静默即可 —— 用户没有需要处置的事。
    recordView.mutate(slug)
  }, [slug, query.data, recordView])

  if (query.isError && query.data === undefined) {
    // 404 是常态而非故障：帖子可能被下架、归档或删除，后端一律 404。
    if (hasProblemCode(query.error, 'not_found')) {
      return (
        <div className="mx-auto max-w-xl py-16 text-center">
          <h1 className="text-lg font-semibold text-foreground">内容不存在或已下架</h1>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            这个链接指向的内容可能已被发布者下架、归档或删除。
          </p>
          <ButtonLink to="/" className="mt-5">
            返回首页
          </ButtonLink>
        </div>
      )
    }
    return <ErrorState error={query.error} onRetry={query.refetch} />
  }

  if (!query.data) return <PostDetailSkeleton />

  return <PostDetailContent detail={query.data} />
}

function PostDetailContent({ detail }: { detail: PublicPostDetail }) {
  usePageMeta({
    title: `${detail.title} - ${SITE_NAME}`,
    description: detail.summary,
    path: `/mods/${detail.slug}`,
  })

  const { status } = useAuth()
  const isSignedIn = status === 'authenticated'
  const toggleFavorite = useToggleFavoriteMutation(detail.slug)

  const authorLine = formatAuthorLine(detail)
  const release = detail.release
  // 五栏标签（类型 / 作者 / 游戏 / 其他 / 更新时间），见 api/adapters.ts。
  const facets = useMemo(() => toTagFacets(detail), [detail])
  /*
    正文里要遮的那几张图。用 useMemo 是因为它是 `RenderedHtmlContent` 里那个
    effect 的依赖：每渲染一次就造一个新 Set，会让那个 effect 每次都重跑一遍
    整段正文的 DOM。
  */
  const sensitiveUrls = useMemo(() => toSensitiveImageUrls(detail), [detail])
  const primaryType = facets.type[0]
  const updated = facets.updated[0]
  /*
    header 里的「别名」那一行（后端 2026-09-05）。标题本身不在这里重复，
    上面那个 h1 已经有它，这行只回答「这一页还能用哪些名字找到」。
  */
  const titleAliases = detail.title_aliases

  const onToggleFavorite = () => {
    if (!isSignedIn) {
      // 后端首期不支持匿名收藏（PUT 会 401）。提前说清楚，别让用户白点一次。
      notifyError('登录后才能收藏')
      return
    }
    const next = !detail.favorited
    toggleFavorite.mutate(next, {
      onSuccess: () => notifySuccess(next ? '已加入收藏' : '已取消收藏'),
      onError: (error) => notifyError(toMessage(error, '操作失败，请重试')),
    })
  }

  return (
    <div className="space-y-4 pb-20 lg:flex lg:gap-5 lg:space-y-0 lg:pb-0">
      {/* ---------------------------------------------------- 中间主内容 */}
      <div className="min-w-0 flex-1 space-y-4">
        <Panel className="space-y-4">
          <header className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              {/*
                设计稿在标题上方有「整合包」「v2.4.1」两个徽章。
                前者取「类型」栏的第一个值（后端 `tag.category === 'type'`，
                见 api/taxonomy.ts）；后者用 release.version_label。
              */}
              {primaryType && <Badge tone="primary">{primaryType.label}</Badge>}
              {release && <Badge tone="outline">{release.version_label}</Badge>}
            </div>

            <h1 className="text-xl font-bold tracking-tight text-foreground sm:text-2xl">
              {detail.title}
            </h1>

            {/*
              别名。放在标题与社团行之间，回答「这一页还能用哪些名字找到」。

              不做成链接：它们不是独立的实体，检索时由自由文本 `q` 认它们，
              做成可点的会让人以为点开是另一个列表。
            */}
            {titleAliases.length > 0 && (
              <p className="text-sm text-muted-foreground sm:text-[0.9375rem]">
                {titleAliases.join('、')}
              </p>
            )}

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground sm:text-[0.8125rem]">
              {authorLine && (
                <span className="inline-flex items-center gap-1.5">
                  <UserIcon className="size-3.5 text-subtle-foreground" />
                  {/* 作者名链到作者页；多作者时整行文案里只有第一个可点。 */}
                  {detail.authors[0] ? (
                    <Link
                      to={`/creators/${detail.authors[0].public_id}`}
                      className="hover:text-primary hover:underline"
                    >
                      {authorLine}
                    </Link>
                  ) : (
                    authorLine
                  )}
                </span>
              )}
              {/*
                更新时间是作者手动标注的 `YYYY-MM`，与后端自动记录的修订发布时间
                是两回事：前者说的是「这个作品更新到哪个版本了」，后者说的是
                「这个页面最后一次被编辑是什么时候」。这里显示作者标注的那个，
                点击落到整年列表；后端那个值仍然在右栏的元信息表里。
                标注缺失时退回到修订时间，不留空。
              */}
              <span className="inline-flex items-center gap-1.5">
                <ClockIcon className="size-3.5 text-subtle-foreground" />
                {updated ? (
                  <Link to={updated.to} className="hover:text-primary hover:underline">
                    更新于 {updated.label}
                  </Link>
                ) : (
                  <time dateTime={detail.current_revision_published_at}>
                    {formatDate(detail.current_revision_published_at)}
                  </time>
                )}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <ViewsIcon className="size-3.5 text-subtle-foreground" />
                {formatCompactCount(detail.view_count)}
              </span>
            </div>
          </header>

          {/* 图集：封面在前，其后是正文图片按文档顺序，整体按资产去重。 */}
          <Gallery images={toGalleryImages(detail)} title={detail.title} />

          {/* 桌面端操作区（移动端在吸底栏，见文件末尾） */}
          <div className="hidden gap-2 sm:flex">
            {release ? (
              <ButtonLink
                to={`/mods/${detail.slug}/download`}
                size="lg"
                className="flex-1"
                leadingIcon={<DownloadIcon className="size-[1.125rem]" />}
              >
                下载 · {formatBytes(release.total_size_bytes)}
              </ButtonLink>
            ) : (
              <Button size="lg" className="flex-1" disabled>
                暂无可下载版本
              </Button>
            )}
            <FavoriteButton
              favorited={detail.favorited}
              pending={toggleFavorite.isPending}
              onClick={onToggleFavorite}
              size="lg"
            />
          </div>
        </Panel>

        {/* ------------------------------------------------------ 简介 */}
        <Panel className="space-y-3">
          <h2 className="text-sm font-semibold text-foreground">简介</h2>
          {detail.rendered_html ? (
            /*
              `rendered_html` 是后端**发布时**渲染并缓存的 HTML，不是即时渲染。
              内容源是 Tiptap JSON，经过服务端白名单校验（节点/标记/属性白名单、
              禁止图片外链、链接协议限制，见 internal/content/tiptap.go）。
              也就是说：**清洗在服务端已经完成**，这里不需要再引一个前端消毒库，
              引了反而可能把合法标记洗掉。

              `RenderedHtmlContent` 负责剩下两件与「原样注入」相关的事：把图片的
              根相对路径改写到本部署的 API 基址上（跨源部署下否则会解析到前端
              域名），以及让图片可以点开看大图。
            */
            <RenderedHtmlContent
              html={detail.rendered_html}
              title={detail.title}
              sensitiveUrls={sensitiveUrls}
            />
          ) : (
            <p className="text-sm text-muted-foreground">发布者未填写正文说明。</p>
          )}

          {detail.resource_description && (
            <div className="rounded-card border border-border bg-surface-muted/50 p-3">
              <p className="text-xs font-medium text-muted-foreground">资源说明</p>
              <p className="mt-1 whitespace-pre-line text-sm leading-6 text-foreground">
                {detail.resource_description}
              </p>
            </div>
          )}
        </Panel>

        <CommentSection
          commentsEnabled={detail.comments_enabled}
          commentCount={detail.comment_count}
          isSignedIn={isSignedIn}
        />
      </div>

      {/* ------------------------------------------------------ 右栏 */}
      <aside className="w-full shrink-0 space-y-3 lg:w-72 xl:w-80">
        {/* 广告位暂未启用，先隐藏但保留结构，方便后续直接开启。 */}
        <AdSlot className="hidden" />
        <TagGroups facets={facets} />

        {/* 元信息表（设计稿 D2 右栏底部「文件大小 / 分卷数 / 下载量」） */}
        <Panel className="space-y-2">
          <dl className="space-y-2 text-sm">
            {release ? (
              <>
                <FactRow label="文件大小" value={formatBytes(release.total_size_bytes)} />
                {/*
                  多文件夹时说「组」而不是「分卷」：`.001` / `.002` 这套编号只在
                  同一个文件夹内连续，跨文件夹的几份东西互不相干。把它们加总成
                  「分卷数 5」会让读者以为那是一套要拼在一起的集合，
                  然后在解压失败时以为文件坏了。
                */}
                {release.folder_count > 1 ? (
                  <FactRow
                    label="文件组数"
                    value={`${release.folder_count} 组 · 共 ${release.volume_count} 个文件`}
                  />
                ) : (
                  <FactRow label="分卷数" value={String(release.volume_count)} />
                )}
                {/*
                  「压缩格式」这一行去掉了。后端既不读也不改资源字节，
                  照一个恒定值标注格式，等于对读者断言一件我们并不知道成不成立的事。
                  真实文件名在下载页的每一行上。
                */}
              </>
            ) : (
              <FactRow label="资源" value="暂无可下载版本" />
            )}
            <FactRow label="下载量" value={formatCompactCount(detail.download_count)} />
            <FactRow label="收藏量" value={formatCompactCount(detail.favorite_count)} />
            <FactRow label="首次发布" value={formatDate(detail.first_published_at)} />
            {/* 后端记录的修订时间。它不是作者标注的「更新时间」，两者都要保留。 */}
            <FactRow label="最近修订" value={formatDate(detail.current_revision_published_at)} />
          </dl>
        </Panel>
      </aside>

      {/*
        移动端吸底操作栏（设计稿 M4 底部）。
        `pb-[env(safe-area-inset-bottom)]` 是为了避开 iPhone 的手势条 ——
        少了它，最后那一排按钮会被系统 UI 压掉一部分。
      */}
      <div className="fixed inset-x-0 bottom-0 z-30 flex items-center gap-2 border-t border-border bg-background/95 p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] backdrop-blur sm:hidden">
        <FavoriteButton
          favorited={detail.favorited}
          pending={toggleFavorite.isPending}
          onClick={onToggleFavorite}
          size="md"
        />
        {release ? (
          <ButtonLink
            to={`/mods/${detail.slug}/download`}
            className="flex-1"
            leadingIcon={<DownloadIcon className="size-4" />}
          >
            下载 · {formatBytes(release.total_size_bytes)}
          </ButtonLink>
        ) : (
          <Button className="flex-1" disabled>
            暂无可下载版本
          </Button>
        )}
      </div>
    </div>
  )
}

function FavoriteButton({
  favorited,
  pending,
  onClick,
  size,
}: {
  favorited: boolean
  pending: boolean
  onClick: () => void
  size: 'md' | 'lg'
}) {
  return (
    <Button
      variant="secondary"
      size={size}
      onClick={onClick}
      loading={pending}
      // aria-pressed 让读屏器能念出「已收藏 / 未收藏」这个开关状态。
      aria-pressed={favorited}
      leadingIcon={
        pending ? undefined : (
          <FavoriteIcon
            className={favorited ? 'size-[1.125rem] fill-current text-primary' : 'size-[1.125rem]'}
          />
        )
      }
      className={favorited ? 'text-primary' : undefined}
    >
      <span className={size === 'md' ? 'sr-only sm:not-sr-only' : undefined}>
        {favorited ? '已收藏' : '收藏'}
      </span>
    </Button>
  )
}

function FactRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-semibold tabular-nums text-foreground">{value}</dd>
    </div>
  )
}

/** 骨架屏尺寸与真实布局对齐，避免加载完成时整页跳动。 */
function PostDetailSkeleton() {
  return (
    <div className="space-y-4 lg:flex lg:gap-5 lg:space-y-0">
      <div className="min-w-0 flex-1 space-y-4">
        <Panel className="space-y-4">
          <div className="space-y-2">
            <Skeleton className="h-5 w-24" />
            <Skeleton className="h-7 w-2/3" />
            <Skeleton className="h-4 w-1/2" />
          </div>
          <Skeleton className="aspect-[16/9] w-full rounded-card" />
          <div className="grid grid-cols-4 gap-2">
            {Array.from({ length: 4 }, (_, index) => (
              <Skeleton key={index} className="aspect-[16/10] rounded-lg" />
            ))}
          </div>
          <Skeleton className="h-12 w-full rounded-card" />
        </Panel>
        <Panel className="space-y-2">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-11/12" />
          <Skeleton className="h-4 w-3/4" />
        </Panel>
      </div>
      <aside className="w-full shrink-0 space-y-3 lg:w-72 xl:w-80">
        <Skeleton className="h-40 w-full rounded-panel" />
        <Skeleton className="h-56 w-full rounded-panel" />
      </aside>
    </div>
  )
}
